import type { CSSProperties, JSX, ReactNode } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import {
    ChartsTooltipContainer,
    type ChartsTooltipProps,
    useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { DateTime } from 'luxon';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { css } from '@emotion/css';
import { OperatorTypo } from '../ui/OperatorTypo.ts';
import { ColorHex } from '../ui/Colors.ts';
import { Language } from 'dg-web-shared/lib/Localized.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import * as NumberFormatter from 'dg-web-shared/lib/NumberFormatter.ts';
import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';

export const enum DataPointUnit {
    PCS = 'PCS',
    CHF = 'CHF',
}

type AxisTickLabelProps = {
    x?: number | string;
    y?: number | string;
    text?: string;
    style?: unknown;
};

export function OperatorBarChart({
    data,
    dataKeys,
    tooltipNames,
    totalKey,
    yearMonthKey,
    unit = DataPointUnit.PCS,
    maxYValue,
}: {
    data: object[];
    dataKeys: string[];
    tooltipNames: string[];
    totalKey?: string;
    yearMonthKey: string;
    unit?: DataPointUnit;
    maxYValue?: number;
}): JSX.Element {
    const { language } = useLanguage();
    const theme = useTheme();
    const dataset = data as Array<Record<string, number | string>>;
    const currentMonth = DateTime.now();
    const monthByLabel = new Map<string, DateTime>();
    dataset.forEach(entry => {
        const rawValue = entry[yearMonthKey];
        if (typeof rawValue !== 'string') {
            return;
        }
        const tickMonth = DateTime.fromISO(rawValue);
        if (!tickMonth.isValid) {
            return;
        }
        monthByLabel.set(formatMonthLabel(tickMonth, language), tickMonth);
    });

    const tickLabelStyle = {
        ...OperatorTypo.subcaption,
        textTransform: 'uppercase',
        fill: ColorHex.typo,
        fillOpacity: 0.6,
    };
    const yAxisTickLabelStyle = {
        ...Typo.captionC1,
        fontWeight: 900,
        fill: ColorHex.typo,
        fillOpacity: 0.6,
    };

    const series = dataKeys.map(key => ({
        id: key,
        dataKey: key,
        color: colorMap[key] ?? ColorHex.typo,
        stack: 'total',
    }));

    const AxisTickLabel = (tickProps: AxisTickLabelProps) => (
        <ChartMonthTick
            {...tickProps}
            monthByLabel={monthByLabel}
            currentMonth={currentMonth}
        />
    );

    const TooltipContent = (props: ChartsTooltipProps) => {
        const tooltip = useAxesTooltip();
        const dataIndex = tooltip?.[0]?.dataIndex;
        const entry = dataIndex === undefined ? undefined : dataset[dataIndex];
        const total = totalKey && entry ? Number(entry[totalKey] ?? 0) : null;
        const isEmptyMonth = total === 0;

        return (
            <ChartsTooltipContainer {...props} trigger="axis">
                {entry && !isEmptyMonth && (
                    <Box
                        sx={{
                            padding: 1,
                            backgroundColor: 'rgb(50, 73, 107, 0.8)',
                            color: theme => theme.palette.white.main,
                        }}
                    >
                        {total && (
                            <TooltipItem
                                variant={'subtitle2'}
                                name={
                                    <Localized
                                        de="Gesamt"
                                        fr="Totale"
                                        it="Totale"
                                        en="Total"
                                    />
                                }
                                value={total}
                                unit={unit}
                            />
                        )}
                        {dataKeys
                            .map((key, index) => {
                                const value = Number(entry[key] ?? 0);
                                if (value) {
                                    return (
                                        <TooltipItem
                                            variant={'body2'}
                                            key={key}
                                            name={tooltipNames[index]}
                                            value={value}
                                            unit={unit}
                                        />
                                    );
                                }
                                return null;
                            })
                            .reverse()}
                    </Box>
                )}
            </ChartsTooltipContainer>
        );
    };

    return (
        <div style={{ width: '100%', height: 400 }}>
            <BarChart
                dataset={dataset}
                height={400}
                margin={{ top: 10, bottom: 5 }}
                grid={{ horizontal: true, vertical: false }}
                axisHighlight={{ x: 'band' }}
                xAxis={[
                    {
                        id: 'month',
                        dataKey: yearMonthKey,
                        scaleType: 'band',
                        tickLabelStyle,
                        tickLabelInterval: () => true,
                        disableTicks: true,
                        categoryGapRatio: 0.35,
                        barGapRatio: 0,
                        valueFormatter: (value: string | number) => {
                            if (typeof value === 'string') {
                                const tickMonth = DateTime.fromISO(value);
                                if (tickMonth.isValid) {
                                    return formatMonthLabel(
                                        tickMonth,
                                        language,
                                    );
                                }
                            }
                            return String(value ?? '');
                        },
                        slots: {
                            axisTickLabel: AxisTickLabel,
                        },
                    },
                ]}
                yAxis={[
                    {
                        id: 'value',
                        position: 'right',
                        min: 0,
                        max: maxYValue
                            ? roundToNearestTick(maxYValue)
                            : undefined,
                        tickNumber: 5,
                        valueFormatter: (value: number) =>
                            numberFormatter(value, unit, false),
                        tickLabelStyle: yAxisTickLabelStyle,
                        disableLine: true,
                        disableTicks: true,
                        width: 80,
                    },
                ]}
                series={series}
                slotProps={{ tooltip: { trigger: 'axis' } }}
                slots={{ tooltip: TooltipContent }}
                sx={{
                    '.MuiChartsAxisHighlight-root': {
                        fill: theme.palette.blue.light,
                    },
                }}
            />
        </div>
    );
}

function TooltipItem({
    variant,
    name,
    value,
    unit,
}: {
    variant?: TypographyProps['variant'];
    name: ReactNode;
    value: number;
    unit: DataPointUnit;
}) {
    return (
        <Typography
            variant={variant}
            sx={{
                display: 'flex',
                marginBottom: variant === 'subtitle2' ? 1 : 0,
            }}
        >
            <Box sx={{ flexGrow: 1, paddingRight: 1 }}>{name}</Box>
            <div>
                {numberFormatter(value, unit, unit === DataPointUnit.CHF)}
            </div>
        </Typography>
    );
}

function ChartMonthTick({
    monthByLabel,
    currentMonth,
    x,
    y,
    text,
    style,
}: {
    monthByLabel: Map<string, DateTime>;
    currentMonth: DateTime;
    x?: number | string;
    y?: number | string;
    text?: string;
    style?: unknown;
}): JSX.Element {
    const label = text ?? '';
    const tickMonth = monthByLabel.get(label);
    const styleProps = (style ?? {}) as CSSProperties & {
        angle?: number;
    };
    const { angle, ...baseTextStyle } = styleProps;
    const transform =
        angle !== undefined && x !== undefined && y !== undefined
            ? `rotate(${angle}, ${x}, ${y})`
            : undefined;
    const xNumber =
        typeof x === 'number' ? x : Number.isFinite(Number(x)) ? Number(x) : 0;
    const yNumber =
        typeof y === 'number' ? y : Number.isFinite(Number(y)) ? Number(y) : 0;
    const isCurrent =
        !!tickMonth &&
        tickMonth.month === currentMonth.month &&
        tickMonth.year === currentMonth.year;
    const isInFuture = tickMonth ? tickMonth > currentMonth : false;
    const textStyle = {
        ...baseTextStyle,
        fill: isCurrent ? ColorHex.white : ColorHex.typo,
        fillOpacity: getTextOpacity(isInFuture, isCurrent),
    };
    const textAnchor = 'middle' as const;
    const dominantBaseline = 'hanging' as const;
    return (
        <g
            className={css({
                alignmentBaseline: 'middle',
                ...OperatorTypo.subcaption,
                fill: ColorHex.typo,
                fillOpacity: 0.6,
                textAnchor: 'middle',
            })}
        >
            {isCurrent && (
                <rect
                    className={css({
                        fill: ColorHex.lightblue,
                        fillOpacity: 1,
                    })}
                    x={xNumber - 14}
                    y={yNumber - 2}
                    width={28}
                    height={16}
                />
            )}
            <text
                x={x}
                y={y}
                textAnchor={textAnchor}
                dominantBaseline={dominantBaseline}
                style={textStyle}
                transform={transform}
            >
                {label}
            </text>
        </g>
    );
}

function formatMonthLabel(tickMonth: DateTime, language: Language): string {
    return Formatter.monthNameAbbr(tickMonth, language).toUpperCase();
}

function getTextOpacity(isDisabled: boolean, isCurrent: boolean) {
    if (isDisabled) {
        return 0.3;
    }
    if (isCurrent) {
        return 1;
    }
    return 0.6;
}

const colorMap: { [key: string]: string } = {
    revenueTwint: 'rgb(54,54,54)',
    countTwint: 'rgb(54,54,54)',
    revenueParkingpay: 'rgba(0,94,145,0.9)',
    countParkingpay: 'rgba(0,94,145,0.9)',
    revenuePayStation: ColorHex.black,
    countPayStation: ColorHex.black,
    revenueEasypark: 'rgb(250,94,168)',
    countEasypark: 'rgb(250,94,168)',
    revenueCoop: 'rgb(255,180,94)',
    countCoop: 'rgb(255,180,94)',
    revenueSwisspass: 'rgba(255,89,89)',
    countSwisspass: 'rgba(255,89,89)',
    revenuePnR: 'rgb(147,53,66)',
    countPnR: 'rgb(147,53,66)',
    revenueParkingabo: 'rgb(0,185,215)',
    countParkingabo: 'rgb(0,185,215)',
    revenueParkingaboCounter: 'rgb(0,127,195)',
    countParkingaboCounter: 'rgb(0,127,195)',
    revenueQuickcheckout: 'rgb(68,203,75)',
    countQuickcheckout: 'rgb(68,203,75)',
    countTom: 'rgb(50,72,105)',
    revenueTom: 'rgb(50,72,105)',
    countTcs: 'rgb(255, 235, 2)',
    revenueTcs: 'rgb(255, 235, 2)',
};

export function numberFormatter(
    value: string | number,
    unit: DataPointUnit,
    showDecimals: boolean,
) {
    const parsedValue = typeof value === 'string' ? parseInt(value, 10) : value;
    const formattedNumber = NumberFormatter.format(
        parsedValue,
        showDecimals ? '#,###.##' : '#,###',
    );

    switch (unit) {
        case DataPointUnit.PCS:
            return formattedNumber;
        case DataPointUnit.CHF:
            return NumberFormatter.currencyCentsToLocalPrice(
                parsedValue,
                true,
                showDecimals,
            );
    }
}

function roundToNearestTick(value: number): number {
    const intervalSize = 10 ** Math.floor(Math.log10(value));
    return Math.ceil(Math.ceil(value) / intervalSize) * intervalSize;
}

export function ChartSection({
    title,
    caption,
    children,
}: {
    title: React.ReactNode;
    caption: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <Box sx={{ width: '100%' }}>
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'baseline',
                    marginBottom: 2,
                }}
            >
                <Typography variant="h6">{title}</Typography>
                <Typography variant="h6">{caption}</Typography>
            </Stack>
            {children}
        </Box>
    );
}

export function ScrollableContainer({
    children,
}: {
    children: React.ReactNode;
}) {
    return <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>{children}</Box>;
}
